import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { AdMob, BannerAdPosition, BannerAdSize } from "@capacitor-community/admob";
import { ADMOB_BANNER_UNIT_ID } from "./config.ts";
import { getPanelBanner, isWebRuntime, type AdminBanner } from "./banner.ts";
import { useT } from "../i18n.ts";

/**
 * A banner ad surface. On native Android with an AdMob unit configured
 * (config.ts), this shows/hides a real native banner for as long as the
 * component is mounted — the banner is a native overlay outside the DOM, so
 * this component itself renders nothing in that case.
 *
 * On web (any device) it first checks for an admin-managed "PC 배너"
 * (Firestore `banners`, type "panel" — see gamzone/ads/banner.ts) and shows
 * that if one's active; otherwise it falls back to the plain "광고 영역"
 * placeholder.
 *
 * Exactly one AdSlot should be mounted at a time on native: the plugin owns a
 * single AdView, so two live instances (e.g. MatchScreen's footer slot and
 * App.tsx's info-panel slot both mounted at once) would fight over it. In
 * practice App.tsx's panel slot is only mounted pre-game/hamburger-reopened
 * (mobile) or desktop-width (native app is phone-only), so this doesn't
 * overlap with a live match's own slot except briefly if the hamburger is
 * reopened mid-match.
 */
export function AdSlot({ className = "" }: { className?: string }) {
  const t = useT();
  const showNative = Capacitor.isNativePlatform() && Boolean(ADMOB_BANNER_UNIT_ID);
  const [banner, setBanner] = useState<AdminBanner | null>(null);

  useEffect(() => {
    if (!showNative) return;
    AdMob.showBanner({
      adId: ADMOB_BANNER_UNIT_ID,
      adSize: BannerAdSize.ADAPTIVE_BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      margin: 0,
    }).catch(() => {});
    return () => {
      AdMob.removeBanner().catch(() => {});
    };
  }, [showNative]);

  useEffect(() => {
    if (!isWebRuntime()) return; // native app keeps its own AdMob path above
    let cancelled = false;
    getPanelBanner().then((b) => { if (!cancelled) setBanner(b); });
    return () => { cancelled = true; };
  }, [showNative]);

  if (showNative) return null;

  if (banner) {
    return (
      <a
        className={`gz-ad-slot gz-ad-slot--banner ${className}`.trim()}
        href={banner.linkUrl || "#"}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={banner.imageUrl} alt="" />
      </a>
    );
  }

  return (
    <div className={`gz-ad-slot ${className}`.trim()} aria-hidden="true">
      <span>{t("adArea")}</span>
    </div>
  );
}
